import React, {ComponentProps, memo} from "react";

import classes from "./Timeline.module.css";
import cn from "classnames";
import Skeleton from "../../ui/skeleton";
import {Droppable, DroppableProps} from "react-beautiful-dnd";
import Bubble from "./Bubble";

type Props = Pick<ComponentProps<"div">, "className"> & Omit<DroppableProps, "droppableId"> & {
  droppableId?: DroppableProps["droppableId"];
  extra?: React.ReactNode;
};

const defaultProps = {
  droppableId: "timeline"
};

export default function Timeline({className, children, extra, ...props}: Props & typeof defaultProps) {
  return (
    <div className={cn(classes.root, className)}>
      <div className={classes.line}/>
      <Droppable {...props}>
        {children}
      </Droppable>
      {extra}
    </div>
  );
}

Timeline.defaultProps = defaultProps;

type RowProps = React.ComponentProps<"div"> & {
  narrow?: boolean;
  icon?: React.ReactNode;
  extra?: Record<string, React.ReactNode> | React.ReactNode;
};

Timeline.Row = ({icon, extra, narrow, className, children, ...props}: RowProps) => {
  const renderExtra = () => {
    if (!extra) return null;

    if (typeof extra === "object" && !React.isValidElement(extra)) {
      const buttons = Object.values(extra as Record<string, React.ReactNode>);

      return (
        <div className={classes.rowExtra}>
          <div className={classes.extraButtons}>
            {buttons.map((button, index) => (
              <div key={index} className={classes.extraButton}>
                {button}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className={classes.rowExtra}>
        {extra}
      </div>
    );
  };

  return (
    <div className={cn(classes.row, {[classes.narrowRow]: narrow}, className)} {...props}>
      {icon && (
        <div className={classes.rowIcon}>
          <div className={classes.eraser}/>
          {icon}
        </div>
      )}

      <div className={classes.rowInner}>
        {children}
      </div>

      {renderExtra()}
    </div>
  );
};

Timeline.Skeleton = memo(({children, className, ...props}: ComponentProps<"div">) => (
  <div className={cn(classes.root, className)} {...props}>
    <div className={classes.line}/>

    {children}
  </div>
));

Timeline.SkeletonRow = memo(({width}: { width?: number }) => {
  return (
    <Timeline.Row icon={(
      <Skeleton.Block
        width={24}
        height={24}
        className={classes.skeletonIcon}
      />
    )}>
      <Bubble.Skeleton
        className={classes.skeletonBubble}
        width={width ?? 150}
      />
    </Timeline.Row>
  );
});
